import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { indexMappings } from "../lib/ioMapping";
import { liveTank } from "../lib/tankLevel";
import { useSiteContext } from "../components/layout/Layout";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ChevronDown, ChevronRight } from "lucide-react";

// A 4-20 mA current-loop transmitter never legitimately reports outside this range.
const SENSOR_MIN = 4;
const SENSOR_MAX = 20;
// Fallback full-scale range when a sensor's meta.range_bar isn't configured yet — must be a real
// full-scale value, never an alarm setpoint (pressure_gauge_scaling_bug.md).
const DEFAULT_MAX_PRESSURE_BAR = 40;

// live.value / parameter-history "value" is the raw register value (raw 4-20 mA for a pressure
// transmitter), not bar. Same conversion as Live Monitor / Hydrant / Sprinkler — comparing the
// raw mA number directly against alarm_low_bar/alarm_high_bar (as this page did before) produces
// false LOW/HIGH pressure alarms, since mA and bar are different scales entirely.
function pressureBar(asset, rawValue, ioMap) {
  if (rawValue == null) return null;
  const raw = Number(rawValue);
  const ioMapping = asset.gateway_id && asset.io_point_name ? ioMap?.[`${asset.gateway_id}:${asset.io_point_name}`] : null;
  const maxPressure = ioMapping?.engineering_max != null
    ? Number(ioMapping.engineering_max)
    : (Number(asset.meta?.range_bar) || DEFAULT_MAX_PRESSURE_BAR);
  return Math.min(maxPressure, Math.max(0, ((raw - SENSOR_MIN) / (SENSOR_MAX - SENSOR_MIN)) * maxPressure));
}

// ── Detection master table — maps each asset type to its valid detection signals ──────────
// evaluate(asset, live, ioMap) returns {status, ok, isFault?, hasData:true} or null (not applicable)
// requires: meta field that must be set on the asset for the column to appear
const DETECTION_MASTER = {
  pump: {
    label: "Pumps",
    detections: [
      {
        id: "run", label: "Pump Run",
        evaluate: (_asset, live) => {
          const v = live?.value;
          if (v == null) return null;
          const running = Number(v) === 1;
          return { status: running ? "RUNNING" : "STOPPED", ok: running, hasData: true };
        },
      },
      {
        id: "trip", label: "Pump Trip",
        requires: "pump_trip_io_point_name",
        evaluate: (_asset, live) => {
          const v = live?.extra?.pump_trip?.value;
          if (v == null) return null;
          const tripped = Number(v) === 1;
          return { status: tripped ? "TRIPPED" : "Normal", ok: !tripped, isFault: tripped, hasData: true };
        },
      },
      {
        id: "auto_manual", label: "Auto / Manual",
        requires: "auto_manual_io_point_name",
        evaluate: (_asset, live) => {
          const v = live?.extra?.auto_manual?.value;
          if (v == null) return null;
          const auto = Number(v) === 1;
          return { status: auto ? "AUTO" : "MANUAL", ok: auto, hasData: true };
        },
      },
    ],
  },
  // Tanks: every detection reads the shared calculation (lib/tankLevel.js / live.tank) — the raw
  // signal is mA or V, not a percentage, and used to be compared straight against % thresholds.
  tank: {
    label: "Tanks",
    detections: [
      {
        id: "level", label: "Level",
        evaluate: (asset, live) => {
          const t = liveTank(asset, live);
          if (!t) return null;
          if (t.level_pct == null) {
            return { status: t.status === "SENSOR FAULT" ? "SENSOR FAULT" : "SETUP NEEDED", ok: false, isFault: true, hasData: true };
          }
          const minPct = Number(asset.meta?.min_level_pct || 50);
          return { status: `${t.level_pct.toFixed(1)}%`, ok: t.level_pct >= minPct, isFault: t.alarms.very_low, hasData: true };
        },
      },
      {
        id: "low_level", label: "Low Level",
        requires: "level_low_pct",
        evaluate: (asset, live) => {
          const t = liveTank(asset, live);
          if (!t || t.level_pct == null) return null;
          return { status: t.alarms.low ? "LOW" : "Normal", ok: !t.alarms.low, isFault: t.alarms.low, hasData: true };
        },
      },
      {
        id: "critical_low", label: "Critical Low",
        requires: "level_low_low_pct",
        evaluate: (asset, live) => {
          const t = liveTank(asset, live);
          if (!t || t.level_pct == null) return null;
          return { status: t.alarms.very_low ? "CRITICAL" : "Normal", ok: !t.alarms.very_low, isFault: t.alarms.very_low, hasData: true };
        },
      },
      {
        id: "high_level", label: "High Level",
        requires: "level_high_pct",
        evaluate: (asset, live) => {
          const t = liveTank(asset, live);
          if (!t || t.level_pct == null) return null;
          return { status: t.alarms.high ? "HIGH" : "Normal", ok: !t.alarms.high, isFault: t.alarms.high, hasData: true };
        },
      },
      {
        id: "sensor_fault", label: "Sensor Fault",
        evaluate: (asset, live) => {
          const t = liveTank(asset, live);
          if (!t || t.signal == null) return null;
          // Fault limits from the tank's signal mode (default 3.6 / 21 mA), same as the alarms.
          const fault = t.status === "SENSOR FAULT";
          return { status: fault ? "FAULT" : "Normal", ok: !fault, isFault: fault, hasData: true };
        },
      },
    ],
  },
  pressure_sensor: {
    label: "Pressure Sensors",
    detections: [
      {
        id: "live_pressure", label: "Live Pressure",
        evaluate: (asset, live, ioMap) => {
          if (live?.value == null) return null;
          const pressure = pressureBar(asset, live.value, ioMap);
          return { status: `${pressure.toFixed(2)} bar`, ok: true, hasData: true };
        },
      },
      {
        id: "low_pressure", label: "Low Pressure Alarm",
        requires: "alarm_low_bar",
        evaluate: (asset, live, ioMap) => {
          const threshold = asset.meta?.alarm_low_bar;
          if (live?.value == null || threshold == null) return null;
          const pressure = pressureBar(asset, live.value, ioMap);
          const triggered = pressure <= Number(threshold);
          return { status: triggered ? `LOW ${pressure.toFixed(2)} bar` : "Normal", ok: !triggered, isFault: triggered, hasData: true };
        },
      },
      {
        id: "high_pressure", label: "High Pressure Alarm",
        requires: "alarm_high_bar",
        evaluate: (asset, live, ioMap) => {
          const threshold = asset.meta?.alarm_high_bar;
          if (live?.value == null || threshold == null) return null;
          const pressure = pressureBar(asset, live.value, ioMap);
          const triggered = pressure >= Number(threshold);
          return { status: triggered ? `HIGH ${pressure.toFixed(2)} bar` : "Normal", ok: !triggered, isFault: triggered, hasData: true };
        },
      },
      {
        id: "sensor_fault", label: "Sensor Fault",
        evaluate: (_asset, live) => {
          if (!live) return null;
          const v = Number(live.value);
          if (isNaN(v)) return null;
          const fault = v < 0;
          return { status: fault ? "FAULT" : "Normal", ok: !fault, isFault: fault, hasData: true };
        },
      },
    ],
  },
  mcc_panel: {
    label: "MCC Panels",
    detections: [
      {
        id: "panel_healthy", label: "Panel Healthy",
        evaluate: (_asset, live) => {
          const v = live?.value;
          if (v == null) return null;
          const healthy = Number(v) === 1;
          return { status: healthy ? "HEALTHY" : "FAULT", ok: healthy, isFault: !healthy, hasData: true };
        },
      },
      {
        id: "power_status", label: "Power Available",
        requires: "power_status_io_point_name",
        evaluate: (_asset, live) => {
          const v = live?.extra?.power_status?.value;
          if (v == null) return null;
          const available = Number(v) === 1;
          return { status: available ? "AVAILABLE" : "POWER FAIL", ok: available, isFault: !available, hasData: true };
        },
      },
    ],
  },
  fire_alarm_device: {
    label: "Fire Alarm Devices",
    detections: [
      {
        id: "alarm", label: "Alarm / Fault",
        evaluate: (_asset, live) => {
          const v = live?.value;
          if (v == null) return null;
          const alarm = Number(v) === 1;
          return { status: alarm ? "ALARM" : "Normal", ok: !alarm, isFault: alarm, hasData: true };
        },
      },
    ],
  },
  fan: {
    label: "Fans & AHU",
    detections: [
      {
        id: "run", label: "Run Status",
        evaluate: (_asset, live) => {
          const v = live?.value;
          if (v == null) return null;
          const running = Number(v) === 1;
          return { status: running ? "RUNNING" : "STOPPED", ok: running, hasData: true };
        },
      },
    ],
  },
  valve: {
    label: "Valves",
    detections: [
      {
        id: "open_status", label: "Position",
        evaluate: (_asset, live) => {
          const v = live?.value;
          if (v == null) return null;
          const open = Number(v) === 1;
          return { status: open ? "OPEN" : "CLOSED", ok: true, hasData: true };
        },
      },
    ],
  },
  lift: {
    label: "Lifts",
    detections: [
      {
        id: "floor", label: "Current Floor",
        evaluate: (_asset, live) => {
          const v = live?.value;
          if (v == null) return null;
          return { status: `Floor ${Number(v)}`, ok: true, hasData: true };
        },
      },
    ],
  },
};

function KPI({ label, value, tone = "orange" }) {
  const c = { orange: "from-orange-500 to-pink-500", green: "from-emerald-400 to-emerald-600", blue: "from-sky-400 to-indigo-500", red: "from-red-500 to-pink-600" };
  return (
    <div className="glass p-4 rounded-lg">
      <div className={`text-2xl font-extrabold bg-gradient-to-r ${c[tone]} bg-clip-text text-transparent`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">{label}</div>
    </div>
  );
}

function DetectionBadge({ result }) {
  if (result === null) return <span className="text-gray-400 text-xs font-mono">—</span>;
  if (result.isFault) return <Badge className="bg-red-50 border border-red-200 text-red-700 text-[10px] status-pulse-red">{result.status}</Badge>;
  if (!result.ok) return <Badge className="bg-amber-50 border border-amber-200 text-amber-700 text-[10px]">{result.status}</Badge>;
  return <Badge className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px]">{result.status}</Badge>;
}

function DetectionSection({ config, assets, liveValues, ioMap }) {
  // Only show detection columns that at least one asset has configured (for `requires` columns)
  const activeDetections = config.detections.filter((d) => {
    if (!d.requires) return true;
    return assets.some((a) => a.meta?.[d.requires] != null);
  });

  const hasAnyFault = assets.some((asset) => {
    const live = liveValues[asset.asset_id];
    return config.detections.some((d) => d.evaluate(asset, live, ioMap)?.isFault);
  });

  return (
    <div className="glass rounded-lg overflow-hidden">
      <div className={`px-5 py-3 flex items-center justify-between border-b border-gray-200 ${hasAnyFault ? "bg-red-500/10" : ""}`}>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold uppercase tracking-wider">{config.label}</span>
          <Badge variant="outline" className="text-[10px] border-gray-300 text-gray-500">{assets.length}</Badge>
        </div>
        {hasAnyFault && <Badge className="bg-red-50 border border-red-200 text-red-700 text-[10px]">FAULT DETECTED</Badge>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
              <th className="px-4 py-2 font-semibold">Asset</th>
              <th className="px-4 py-2 font-semibold text-gray-400">Location</th>
              {activeDetections.map((d) => (
                <th key={d.id} className="px-4 py-2 font-semibold">{d.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {assets.map((asset, i) => {
              const live = liveValues[asset.asset_id];
              return (
                <tr key={asset.asset_id} className={`border-t border-gray-200 ${i % 2 ? "bg-white/[0.015]" : ""}`}>
                  <td className="px-4 py-2.5 font-medium">{asset.name}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{asset.location_label || asset.location || "—"}</td>
                  {activeDetections.map((d) => (
                    <td key={d.id} className="px-4 py-2.5">
                      <DetectionBadge result={d.evaluate(asset, live, ioMap)} />
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function DetectionPage() {
  const { selectedSite, sites } = useSiteContext();
  const [panels, setPanels] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [allAssets, setAllAssets] = useState([]);
  const [liveValues, setLiveValues] = useState({});
  const [ioMap, setIoMap] = useState({});

  useEffect(() => {
    if (!selectedSite) return;
    const load = () => {
      api.get("/panels", { params: { site_id: selectedSite } }).then((r) => setPanels(r.data || [])).catch(() => {});
      api.get(`/assets`, { params: { site_id: selectedSite } }).then((r) => setAllAssets(r.data || [])).catch(() => {});
      api.get(`/assets/live-values`, { params: { site_id: selectedSite } }).then((r) => setLiveValues(r.data || {})).catch(() => {});
    };
    load();
    const i = setInterval(load, 8000);
    return () => clearInterval(i);
  }, [selectedSite]);

  // IO Mappings give pressure sensors' real calibrated full-scale range (engineering_max) — same
  // source Live Monitor / Hydrant / Sprinkler use, so alarm evaluation here agrees with them.
  useEffect(() => {
    const gwIds = [...new Set(allAssets.filter((a) => a.asset_type === "pressure_sensor").map((a) => a.gateway_id).filter(Boolean))];
    if (!gwIds.length) { setIoMap({}); return; }
    Promise.all(gwIds.map((gwId) => api.get(`/hardware/gateways/${gwId}/io-mappings`).then((r) => r.data || []).catch(() => [])))
      .then((lists) => {
        const map = {};
        Object.assign(map, indexMappings(lists.flat()));
        setIoMap(map);
      });
  }, [allAssets]);

  const site = sites.find((s) => s.site_id === selectedSite);

  // Group assets by type, only include types with a DETECTION_MASTER entry
  const assetsByType = useMemo(() => {
    const groups = {};
    for (const asset of allAssets) {
      const t = asset.asset_type;
      if (DETECTION_MASTER[t]) {
        if (!groups[t]) groups[t] = [];
        groups[t].push(asset);
      }
    }
    return groups;
  }, [allAssets]);

  // Count active faults across all detections
  const totalFaults = useMemo(() => {
    let n = 0;
    for (const [assetType, config] of Object.entries(DETECTION_MASTER)) {
      const assets = assetsByType[assetType] || [];
      for (const asset of assets) {
        const live = liveValues[asset.asset_id];
        for (const d of config.detections) {
          if (d.evaluate(asset, live, ioMap)?.isFault) n++;
        }
      }
    }
    return n;
  }, [assetsByType, liveValues, ioMap]);

  const totalMonitored = allAssets.filter((a) => a.gateway_id && a.io_point_name).length;

  return (
    <div className="space-y-6" data-testid="detection-page">
      <div>
        <div className="text-xs text-gray-500">Dashboard → {site?.name} → Detection System</div>
        <h1 className="text-3xl md:text-4xl font-extrabold">Detection System</h1>
        <p className="text-sm text-gray-500 mt-1">GR Appendix e · per-asset-type detection · auto-refresh 8s</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI label="Fire Panels" value={panels.length} tone="blue" />
        <KPI label="Monitored Assets" value={totalMonitored} tone="orange" />
        <KPI label="Active Faults" value={totalFaults} tone={totalFaults ? "red" : "green"} />
        <KPI label="Asset Types" value={Object.keys(assetsByType).length} tone="green" />
      </div>

      {/* Per-asset-type detection sections — ordered by DETECTION_MASTER key order */}
      {Object.entries(DETECTION_MASTER).map(([assetType, config]) => {
        const assets = assetsByType[assetType];
        if (!assets?.length) return null;
        return (
          <DetectionSection
            key={assetType}
            config={config}
            assets={assets}
            liveValues={liveValues}
            ioMap={ioMap}
          />
        );
      })}

      {!allAssets.length && (
        <div className="glass rounded-lg p-10 text-center text-gray-500 text-sm">
          No assets registered for this site. <a href="/registry" className="text-orange-600 hover:text-orange-700 font-semibold">Add assets in Registry</a>
        </div>
      )}

      {/* Fire Alarm Panels — kept separate as they are not asset-registry entities */}
      {panels.length > 0 && (
        <div className="glass rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200 font-semibold">Fire Alarm Panels</div>
          <Table>
            <TableHeader>
              <TableRow className="border-gray-200 hover:bg-transparent">
                <TableHead className="w-10"></TableHead>
                <TableHead>Panel ID</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Zone</TableHead>
                <TableHead>Floor</TableHead>
                <TableHead>Last Comm</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {panels.map((p) => (
                <React.Fragment key={p.panel_id}>
                  <TableRow
                    className="border-gray-200 cursor-pointer"
                    onClick={() => setExpanded(expanded === p.panel_id ? null : p.panel_id)}
                  >
                    <TableCell>{expanded === p.panel_id ? <ChevronDown className="w-4 h-4" strokeWidth={2.5} /> : <ChevronRight className="w-4 h-4" strokeWidth={2.5} />}</TableCell>
                    <TableCell className="font-mono text-xs text-orange-700">{p.panel_id}</TableCell>
                    <TableCell className="font-semibold">{p.panel_name}</TableCell>
                    <TableCell><Badge variant="outline" className="border-gray-200 text-xs">{p.panel_type}</Badge></TableCell>
                    <TableCell className="text-xs">{p.zone}</TableCell>
                    <TableCell>{p.floor_number}</TableCell>
                    <TableCell className="text-xs text-emerald-400">{p.last_communication ? new Date(p.last_communication).toLocaleTimeString("en-IN") : "—"}</TableCell>
                    <TableCell>
                      <Badge className={`border-0 ${p.status === "Working" ? "bg-emerald-50 border border-emerald-200 text-emerald-700" : "bg-red-50 border border-red-200 text-red-700"}`}>{p.status}</Badge>
                    </TableCell>
                  </TableRow>
                  {expanded === p.panel_id && (
                    <TableRow className="border-gray-200">
                      <TableCell colSpan={8} className="bg-black/30 p-4">
                        <div className="text-xs text-gray-500 text-center py-2">
                          No live zone data — link addressable points in the Addressable Panel page to see a real breakdown.
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
              {!panels.length && <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">No panels for this site.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
